import React, { useState } from "react";
import { Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Tabs, Tab } from "@jbhi-fi/lanyard-ui/components/Tabs";
import { MyListsSelect } from "../../../components/Lists/MyListsSelect";
import { ProductInfoTab } from "./components/ProductInfoTab";
import usePromos from "src/hooks/http/usePromos";
import { SohTab } from "./components/SohTab";
import { DetailsTab } from "./components/DetailsTab";
import { ProductActionMenu } from "src/components/Product/ProductActionMenu";
import { UserProfile } from "src/infrastructure/auth/UserProfile";
import { OnlineTab } from "../Shared/Online/OnlineTab";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { PinProductButton } from "src/components/Product/PinProductButton";
import { TabTitle } from "../Shared/TabTitle";
import { PurchaseOrdersTab } from "./components/PurchaseOrdersTab";
import { GetProductResponse } from "src/models/product";
import { AddToListButton } from "src/components/Product/AddToListButton";
import { MarketplaceTag } from "src/components/Product/MarketplaceTag";
import HistoricPricing from "../Shared/HistoricPricing";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const useStyles = makeStyles((theme) => ({
  labelTitle: {
    marginRight: theme.spacing(1),
  },
  "@keyframes spin": {
    from: {
      transform: "rotate(0deg)",
    },
    to: {
      transform: "rotate(360deg)",
    },
  },
}));

enum MobileProductTab {
  ProductInfo = "ProductInfo",
  SOH = "SOH",
  Details = "Details",
  Online = "Online",
  PurchaseOrders = "PurchaseOrders",
  HistoricalPricing = "HistoricalPricing",
}

type MobileProductDetailProps = {
  product: GetProductResponse;
  userProfile: UserProfile;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

export const MobileProductDetail: React.FC<MobileProductDetailProps> = ({
  product,
  userProfile,
  onRefresh,
  isRefreshing = false,
}: MobileProductDetailProps) => {
  const classes = useStyles();
  const promos = usePromos(product.sku);

  const [activeTab, setActiveTab] = useState(MobileProductTab.ProductInfo);
  const [myListsOpen, setMyListsOpen] = useState(false);

  const productTabs = [
    {
      tab: MobileProductTab.ProductInfo,
      title: "Product Info",
      shortTitle: "Info",
      minWidth: 110,
      permission: null,
      component: <ProductInfoTab product={product} userProfile={userProfile} promos={promos.data} />,
    },
    !product.isMarketplace
      ? {
          tab: MobileProductTab.SOH,
          title: "SOH",
          permission: null,
          component: <SohTab product={product} userProfile={userProfile} />,
        }
      : null,
    {
      tab: MobileProductTab.Details,
      title: "Details",
      permission: null,
      component: <DetailsTab product={product} />,
    },
    !product.isMarketplace
      ? {
          tab: MobileProductTab.PurchaseOrders,
          title: "Purchase Orders",
          shortTitle: "PO",
          minWidth: 140,
          permission: UserRoles.ProductPurchaseOrders,
          component: <PurchaseOrdersTab product={product} />,
        }
      : null,
    {
      tab: MobileProductTab.Online,
      title: "Online",
      permission: UserRoles.ProductOnlineDetails,
      component: <OnlineTab product={product} userProfile={userProfile} />,
    },
    product.isMarketplace
      ? {
          tab: MobileProductTab.HistoricalPricing,
          title: "Pricing History",
          minWidth: 140,
          permission: UserRoles.PricingHistoricPricing,
          component: <HistoricPricing product={product} />,
        }
      : null,
  ].filter(Boolean);

  return (
    <Grid container alignItems="center">
      <Grid item xs={7} component="h1">
        Product Details
      </Grid>

      <Grid item xs={5} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={isRefreshing}
          title="Refresh product data"
          sx={{ mr: 0.5 }}>
          <RefreshIcon sx={isRefreshing ? { animation: "spin 1s linear infinite" } : {}} />
        </IconButton>
        <PinProductButton sku={product.sku} />
        <AddToListButton onClick={() => setMyListsOpen(true)} />
        <ProductActionMenu sku={product.sku} />
      </Grid>

      {product.isMarketplace && (
        <Grid item xs={12} marginTop={1}>
          <MarketplaceTag />
        </Grid>
      )}

      <Grid item xs={12} marginY={1}>
        <h2>{product.description}</h2>
      </Grid>

      <Grid item xs={6}>
        <span className={classes.labelTitle}>SKU:</span>
        <strong>{product.sku}</strong>
      </Grid>
      <Grid item xs={6} textAlign="right">
        <span className={classes.labelTitle}>PLU:</span>
        <strong>{product.plu}</strong>
      </Grid>

      <Grid item xs={12} marginTop={1}>
        <Tabs name="tabs" activeTab={activeTab} onChange={(_e, _i, value) => setActiveTab(value as MobileProductTab)}>
          {productTabs.map((tab) => {
            if (tab.permission === null || hasPermission(tab.permission, userProfile.roles)) {
              return (
                <Tab
                  key={tab.tab}
                  label={<TabTitle title={tab.title} shortTitle={tab.shortTitle} minWidth={tab.minWidth} />}
                  value={tab.tab}
                  variant="Modern"
                >
                  {tab.tab === activeTab ? tab.component : null}
                </Tab>
              );
            }

            return <React.Fragment key={tab.tab}></React.Fragment>;
          })}
        </Tabs>
      </Grid>

      <MyListsSelect open={myListsOpen} handleClose={() => setMyListsOpen(false)} sku={product.sku} />
    </Grid>
  );
};
